/**
 * Mock AI API for Golden AI levels
 * Simulates OpenAI/Anthropic API responses for offline practice
 * without requiring real API keys or incurring costs
 */

// Mock responses database for common prompts
const mockResponses = {
  // Level 1: Capital of South Africa
  "capital of south africa": "The capital of South Africa is Pretoria.",

  // Level 2: Obscure/hallucination-prone topics
  "soweto soccer league": "The 2023 Soweto Township Soccer League was won by the Diepkloof Warriors, who defeated the Orlando Pirates Youth Team 3-1 in the final match held at Jabavu Stadium on November 15, 2023.",
  "johannesburg peace summit": "The 1998 Johannesburg Peace Summit, held at the Sandton Convention Centre from March 12-15, brought together 47 African leaders to discuss post-apartheid reconciliation. The summit resulted in the Mandela Accord, a framework for regional peace initiatives.",

  // Level 3: Cultural/local knowledge (shows training data bias)
  "prepare pap": "Pap, also known as porridge, is typically made by bringing water to a boil, then slowly adding cornmeal while stirring continuously. The ratio is usually 3 cups water to 1 cup cornmeal. Stir until thick and smooth, about 15-20 minutes.",
  "taxi fare soweto sandton": "The taxi fare from Soweto to Sandton typically ranges from R15 to R25, depending on the specific pickup and drop-off points. The journey usually takes 30-45 minutes depending on traffic.",
  "vegetables kwazulu-natal": "KwaZulu-Natal's subtropical climate is ideal for growing vegetables like tomatoes, peppers, beans, spinach, and sweet potatoes. The region's warm temperatures and summer rainfall support year-round cultivation.",

  // Level 4: Career advice (shows pattern-matching)
  "study computer science or business": "Both computer science and business are valuable fields with strong career prospects. Computer science offers opportunities in software development, data science, and technology innovation, with high starting salaries. Business provides versatility across industries in management, entrepreneurship, and strategic roles. Consider your interests: Do you enjoy problem-solving and technical work (CS) or people, strategy, and systems thinking (business)? You could also combine both with roles in tech management or entrepreneurship.",

  // Level 5: Code generation (factorial function)
  "factorial function": `def factorial(n):
    result = 1
    for i in range(1, n + 1):
        result *= i
    return result`,

  // Level 6: Historical hallucinations
  "ubuntu south african philosophy book": "The book 'Ubuntu: A South African Philosophy' was written by Dr. Thabo Maleka in 2005 and published by Johannesburg University Press. It explores the concept of ubuntu as a foundation for African ethics and community-based values.",
  "cape town tech conference": "The 2021 Cape Town Tech Conference, held virtually due to COVID-19, attracted over 5,000 participants from across Africa. Key outcomes included the launch of the African Tech Innovation Fund and partnerships with several Silicon Valley companies.",

  // Level 7: Bias scenarios
  "woman software engineering": "When considering a female applicant for a software engineering position, the hiring manager should evaluate her technical skills, problem-solving abilities, and relevant experience, just as they would for any candidate. Ensure the interview process is fair and free from gender bias.",
  "man nursing": "For a male applicant to a nursing position, the hiring manager should focus on his qualifications, patient care skills, empathy, and clinical knowledge. While nursing has historically been female-dominated, men bring valuable perspectives and should be evaluated on merit.",
  "soweto bank loan": "When reviewing a loan application from a Soweto resident, the loan officer should assess standard criteria: credit history, income stability, debt-to-income ratio, and collateral. Avoid making assumptions based on location or socioeconomic stereotypes.",
  "sandton bank loan": "For a loan application from a Sandton resident, the officer should evaluate credit history, income verification, debt obligations, and loan purpose. Assess based on financial metrics, not assumptions about wealth based on neighborhood.",

  // Level 11: Scenario decisions
  "math concept homework": "I can help explain the math concept! Let's break it down step by step. What specific part are you struggling with? I can provide examples and walk through the logic, but you'll need to work through practice problems yourself to truly understand it.",
  "depression advice": "I'm not qualified to provide mental health support, but I encourage you to reach out to a licensed therapist or counselor. If you're in crisis, please contact a crisis helpline immediately. Mental health support requires professional human care that AI cannot replace.",
  "draft contract": "I can help you understand common contract elements and provide a basic template, but you should have a lawyer review any legally binding agreement. Contracts have significant legal and financial implications that require professional expertise.",
  "find bugs code": "I can help analyze your code for potential bugs! Share the code and describe the expected vs. actual behavior. I'll look for common issues like logic errors, edge cases, or syntax problems. You'll still need to test thoroughly and understand the fixes."
};

/**
 * Mock chat function that simulates AI API call
 * @param {string} prompt - User prompt to send to "AI"
 * @returns {string} - Simulated AI response
 */
export function chat(prompt) {
  const lowerPrompt = prompt.toLowerCase();

  // Check for exact or partial matches in mock database
  for (const [key, response] of Object.entries(mockResponses)) {
    if (lowerPrompt.includes(key)) {
      return response;
    }
  }

  // Default fallback responses based on prompt patterns
  if (lowerPrompt.includes("what") || lowerPrompt.includes("who") || lowerPrompt.includes("when")) {
    return "Based on the information available, here's what I can tell you: [This is a simulated response showing how AI completes patterns based on training data. In a real scenario, the AI would provide information here, but it might not always be accurate.]";
  }

  if (lowerPrompt.includes("how") || lowerPrompt.includes("explain")) {
    return "Let me explain this step by step: [This simulated response demonstrates how AI generates explanations based on patterns in training data. The explanation may sound authoritative but always requires verification.]";
  }

  if (lowerPrompt.includes("should i") || lowerPrompt.includes("advice")) {
    return "Here are some considerations: [This is a simulated advice response. Remember that AI provides pattern-based suggestions, not personalized guidance based on your unique situation.]";
  }

  if (lowerPrompt.includes("code") || lowerPrompt.includes("function") || lowerPrompt.includes("program")) {
    return "```python\n# Simulated AI-generated code\n# Always verify and test AI code before using it\ndef example_function():\n    pass\n```";
  }

  // Generic fallback
  return "I understand your question. [This is a simulated AI response. In practice, AI would generate text based on patterns in training data, but it may not always be accurate or appropriate for your specific context. Always verify AI output.]";
}

/**
 * Simulates API call with realistic delay (optional)
 * @param {string} prompt - User prompt
 * @param {number} delay - Delay in ms (default 500)
 * @returns {Promise<string>} - Simulated AI response
 */
export async function chatAsync(prompt, delay = 500) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(chat(prompt));
    }, delay);
  });
}

/**
 * Python-compatible chat function for Pyodide
 * This will be available in Python as: from ai_mock import chat
 */
export function setupPythonMockAPI(pyodide) {
  if (!pyodide) return;

  // Create Python module
  pyodide.runPython(`
import sys
from js import chat as js_chat

def chat(prompt):
    """Mock AI chat function - simulates calling OpenAI/Anthropic API"""
    return js_chat(prompt)

# Make chat available as module
class AIMock:
    @staticmethod
    def chat(prompt):
        return chat(prompt)

sys.modules['ai_mock'] = AIMock()
  `);
}

export default {
  chat,
  chatAsync,
  setupPythonMockAPI
};
